import type { SerializedMeterAddon, SerializedMeterModel, UpdateMeterAddonDto } from '@enmon/commons/lib/tools';
import { useServices } from 'containers/services';
import { useAppNotifications } from 'hooks';
import { useMutation, type UseMutationResult, useQueryClient } from 'react-query';
import { getErrorMessage } from 'utils/getErrorMessage';
import { MUTATION_KEYS, QUERY_KEYS } from 'utils/queryKeys';

type AssignModuleToModelArg = {
  module: SerializedMeterAddon;
  modelId: SerializedMeterModel['id'];
  assign: boolean;
};

type AssignModuleToModelResult = SerializedMeterAddon;

export const useAssignModuleToModel = (): UseMutationResult<
  AssignModuleToModelResult,
  unknown,
  AssignModuleToModelArg
> => {
  const queryClient = useQueryClient();
  const { meterModulesApiClient } = useServices();
  const notify = useAppNotifications();

  return useMutation({
    mutationKey: MUTATION_KEYS.meterModules.assignToModel(),
    mutationFn: async ({ module, modelId, assign }: AssignModuleToModelArg) => {
      const currentModelIds = module.meter_models?.map(model => model.id) ?? [];
      const isAssigned = currentModelIds.includes(modelId);

      if (assign === isAssigned) {
        return module;
      }

      const updatedModelIds = assign ? [...currentModelIds, modelId] : currentModelIds.filter(id => id !== modelId);

      const dtoValues = {
        meter_models: updatedModelIds,
      } satisfies UpdateMeterAddonDto;

      return await meterModulesApiClient.update({ id: module.id, values: dtoValues });
    },
    onError: (error, { assign }) => {
      notify.error({
        message: `There was an error while ${assign ? 'assigning' : 'unassigning'} module to meter model`,
        description: error instanceof Error ? getErrorMessage(error) : undefined,
      });
    },
    onSuccess: (_, { module, assign }) => {
      queryClient.invalidateQueries(QUERY_KEYS.meterModels.all());
      queryClient.invalidateQueries(QUERY_KEYS.meterModules.one(module.id));
      notify.success({
        message: `Module ${assign ? 'assigned to' : 'unassigned from'} meter model successfully`,
      });
    },
  });
};
